import * as React from "react";
import { useTranslation } from "react-i18next";
import { currentNetworkOnlineStatus } from "~/config/capacitor/network/hook";
import { captureMessage } from "~/config/sentry";
import { Button } from "~/design-system/Button/Button";
import { ClovisIcon } from "~/design-system/Icon/icons/outline/ClovisIcon";
import { SpinLoader } from "~/design-system/Spinner/SpinLoader";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text/Text";
import { useAuth0 } from "./use-auth0";

type DefaultOnRedirectingProps = {
  onRetryClick?: () => unknown;
  reason?: "sessionExpired";
};

function DefaultOnRedirecting(props: DefaultOnRedirectingProps): JSX.Element {
  const { error, isAuthenticated, isLoading, user } = useAuth0();
  const [showRetry, setShowRetry] = React.useState(false);
  const { t } = useTranslation();
  const sessionExpired = props.reason === "sessionExpired";

  // Wait 2s before showing the retry button
  React.useEffect(() => {
    setShowRetry(false);
    const timeout = setTimeout(() => {
      setShowRetry(true);
    }, 2000);

    // Log to Sentry if we are still here after 15s
    const sentryTimeout = setTimeout(() => {
      if (!currentNetworkOnlineStatus) return;

      captureMessage("⚠️ DefaultOnRedirecting stuck", {
        extra: {
          error: JSON.stringify(error),
          hasUser: !!user,
          isAuthenticated,
          isLoading,
        },
        level: "warning",
        tags: {
          component: "DefaultOnRedirecting",
          is_authenticated: String(isAuthenticated),
          is_loading: String(isLoading),
          reason: "stuck_loading",
        },
      });
    }, 15000);

    return () => {
      clearTimeout(timeout);
      clearTimeout(sentryTimeout);
    };
  }, [setShowRetry, isLoading, isAuthenticated, user, error]);

  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        height: "100vh",
        width: "100vw",
      }}
    >
      <Stack space={"large"} align={"center"} width={"full"}>
        {/* Creating a div of 0px allow the SpinLoader to stay perfectly vertically centered */}
        <div></div>
        <SpinLoader />
        {sessionExpired ? (
          <Stack space="small" align="center">
            <Text size="xlarge" weight="strong" align="center">
              {t("DefaultOnRedirecting.sessionExpiredTitle")}
            </Text>
            <Text tone="secondary" align="center">
              {t("DefaultOnRedirecting.sessionExpiredDescription")}
            </Text>
          </Stack>
        ) : null}
        <div style={{ visibility: showRetry ? "visible" : "hidden" }}>
          <Button
            onClick={props.onRetryClick}
            variant="solid"
            tone="brandAccent"
            leadingIcon={<ClovisIcon />}
            size="large"
            css={{
              boxShadow:
                "0px 0px 0px 1px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
            }}
            dataClovisActionId="default-on-redirecting"
          >
            <Text weight="strong">
              {sessionExpired
                ? t("DefaultOnRedirecting.sessionExpiredAction")
                : t("DefaultOnRedirecting.retry", "Retry Login")}
            </Text>
          </Button>
        </div>
      </Stack>
    </div>
  );
}
export { DefaultOnRedirecting };
